<script>
import { mapGetters } from 'vuex';

const BAR_COLORS = ['#f97316', '#eab308', '#84cc16', '#22c55e'];

export default {
  name: 'ConnectionQualityIndicator',
  computed: {
    ...mapGetters('phone', {
      quality: 'getConnectionQuality',
      rtt: 'getConnectionRtt',
      jitter: 'getConnectionJitter',
      packetLoss: 'getConnectionPacketLoss',
      qualityReady: 'getConnectionQualityReady',
    }),
    // While the connection is warming up the indicator stays grey (treated as
    // unknown) so a noisy initial reading doesn't flash a misleading quality.
    displayQuality() {
      return this.qualityReady ? this.quality : null;
    },
    barWidth() {
      return 2.5;
    },
    bars() {
      const count = 4;
      const gap = 1.5;
      const totalHeight = 14;
      return Array.from({ length: count }, (_, i) => {
        const height = ((i + 1) / count) * totalHeight;
        return {
          x: i * (this.barWidth + gap),
          y: totalHeight - height,
          height,
        };
      });
    },
    qualityLabel() {
      if (this.displayQuality == null) return '';
      const labels = [
        this.$t('PHONE_CTRLS.CONNECTION_QUALITY_DISCONNECTED'),
        this.$t('PHONE_CTRLS.CONNECTION_QUALITY_POOR'),
        this.$t('PHONE_CTRLS.CONNECTION_QUALITY_FAIR'),
        this.$t('PHONE_CTRLS.CONNECTION_QUALITY_GOOD'),
        this.$t('PHONE_CTRLS.CONNECTION_QUALITY_EXCELLENT'),
      ];
      return labels[this.displayQuality];
    },
    tooltipContent() {
      const base = this.$t('PHONE_CTRLS.CONNECTION_QUALITY');
      if (this.displayQuality == null) return { content: base };
      const parts = [];
      if (this.rtt != null) parts.push(`RTT ${this.rtt}ms`);
      if (this.jitter != null) parts.push(`jitter ${this.jitter}ms`);
      if (this.packetLoss != null) parts.push(`loss ${this.packetLoss}%`);
      const statsLine = parts.length ? `<br>${parts.join(' · ')}` : '';
      return {
        content: `<strong>${base}: ${this.qualityLabel}</strong>${statsLine}`,
        html: true,
      };
    },
  },
  methods: {
    barFill(index) {
      if (this.displayQuality === 0) return '#ef4444';
      if (this.displayQuality == null || index >= this.displayQuality)
        return '#d1d5db';
      return BAR_COLORS[this.displayQuality - 1] || '#d1d5db';
    },
  },
};
</script>

<template>
  <div v-tooltip="tooltipContent" class="connection-quality">
    <svg
      :width="displayQuality === 0 ? 24 : 16"
      :height="14"
      :viewBox="displayQuality === 0 ? '0 0 24 14' : '0 0 16 14'"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        v-for="(bar, index) in bars"
        :key="index"
        :x="bar.x"
        :y="bar.y"
        :width="barWidth"
        :height="bar.height"
        :rx="1"
        :fill="barFill(index)"
      />
      <g v-if="displayQuality === 0">
        <line
          x1="18"
          y1="4"
          x2="23"
          y2="11"
          stroke="#ef4444"
          stroke-width="1.5"
          stroke-linecap="round"
        />
        <line
          x1="23"
          y1="4"
          x2="18"
          y2="11"
          stroke="#ef4444"
          stroke-width="1.5"
          stroke-linecap="round"
        />
      </g>
    </svg>
  </div>
</template>

<style scoped>
.connection-quality {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
}
</style>
